/**
 * Sprint 2 — 6 Mrt 2026
 * BookingDocumentSetPanel — status + actions voor de documentenset van
 * een boekingsformulier (Boekingsformulier + Paklijst + Waardefactuur).
 *
 * Toont per document:
 *   - Status-pill (niet aangemaakt / aangemaakt / verouderd / opnieuw gegenereerd)
 *   - Gegenereerd op / door
 *   - Knop Openen / PDF downloaden (indien beschikbaar)
 *
 * Extra:
 *   - Waarschuwingsbanner wanneer `document_set.is_stale = true`
 *   - Grote knop "Documentenset genereren" (of "Opnieuw genereren" bij stale)
 *
 * Props:
 *   bookingId          — string
 *   canGenerate        — bool (RBAC: owner/admin/manager)
 *   compact            — bool (kleinere padding voor detail-view)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { toast } from "sonner";
import {
  FileText, Package, Receipt, Loader2, ExternalLink, Download,
  RefreshCcw, AlertTriangle, CheckCircle2, CircleDashed, Mail, Archive,
} from "lucide-react";
import { Button } from "../../ui/button";
import DocumentSetEmailDialog from "./DocumentSetEmailDialog";

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL"); } catch { return iso; }
}

const STATUS_LABEL = {
  not_generated: "Niet aangemaakt",
  generated: "Aangemaakt",
  stale: "Verouderd",
  regenerated: "Opnieuw gegenereerd",
  generated_on_demand: "Op aanvraag beschikbaar",
};

const STATUS_STYLE = {
  not_generated: "bg-gray-100 text-gray-700 border-gray-300",
  generated: "bg-emerald-50 text-emerald-800 border-emerald-300",
  regenerated: "bg-emerald-50 text-emerald-800 border-emerald-300",
  stale: "bg-amber-50 text-amber-800 border-amber-300",
  generated_on_demand: "bg-blue-50 text-blue-800 border-blue-300",
};

function StatusPill({ status }) {
  const label = STATUS_LABEL[status] || status;
  const style = STATUS_STYLE[status] || STATUS_STYLE.not_generated;
  const Icon = status === "stale" ? AlertTriangle
             : status === "not_generated" ? CircleDashed
             : CheckCircle2;
  return (
    <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] uppercase font-bold tracking-wide ${style}`}
          data-testid={`docset-status-${status}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

function DocRow({ icon, title, description, status, generatedAt, generatedBy, actions, testid, missing = false }) {
  const Icon = icon;
  const borderClass = missing ? "border-red-400" : "border-gray-200";
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 border ${borderClass} bg-white`}
         data-testid={testid}
         data-missing={missing ? "true" : undefined}>
      <div className="flex items-start gap-2 min-w-0 flex-1">
        <div className="flex-shrink-0 h-8 w-8 flex items-center justify-center bg-[#0A192F] text-white">
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="font-chivo font-bold text-sm text-[#0A192F]">{title}</div>
          {description && <div className="text-[11px] text-gray-500 mt-0.5">{description}</div>}
          <div className="mt-1"><StatusPill status={status} /></div>
          {generatedAt && (
            <div className="text-[11px] text-gray-500 mt-1">
              Gegenereerd op {fmtDateTime(generatedAt)}
              {generatedBy ? ` · door ${generatedBy}` : ""}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 flex-shrink-0">
        {actions}
      </div>
    </div>
  );
}

export default function BookingDocumentSetPanel({
  bookingId,
  canGenerate = false,
  compact = false,
  refreshKey = null,
}) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyDoc, setBusyDoc] = useState(null); // 'packing_list' | 'value_invoice'
  const [state, setState] = useState({
    document_set: null,
    totals: null,
    items_count: 0,
    wants_value_invoice: false,
    value_invoice_currency: "EUR",
    customer_email: null,
    readiness: { ready: false, missing: [], reason: null },
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await adminApi.get(`/admin/booking-forms/${bookingId}/document-set`);
      setState(r.data);
    } catch (e) {
      // Silently — panel zal een lege state tonen
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh, refreshKey]);

  const ds = state.document_set || {
    booking_form: { status: "generated_on_demand" },
    packing_list: { status: "not_generated" },
    value_invoice: { status: "not_generated" },
    is_stale: false,
  };
  const isStale = !!ds.is_stale;
  const noItems = state.items_count === 0;
  const readiness = state.readiness || { ready: false, missing: [], reason: null };
  const setReady = readiness.ready === true;
  const missingKinds = new Set(readiness.missing || []);
  const emailPreview = useMemo(() => {
    const previews = [
      {
        kind: "booking_form",
        filename: `boekingsformulier-${String(bookingId || "").slice(0, 8)}.pdf`,
      },
    ];
    const pl = ds.packing_list || {};
    if (pl.id) {
      previews.push({
        kind: "packing_list",
        filename: `paklijst-${String(pl.id).slice(0, 8)}.pdf`,
      });
    }
    const vi = ds.value_invoice || {};
    if (vi.id) {
      previews.push({
        kind: "value_invoice",
        filename: `waardefactuur-${vi.invoice_number || String(vi.id).slice(0, 8)}.pdf`,
      });
    }
    return previews;
  }, [bookingId, ds.packing_list, ds.value_invoice]);

  const downloadZip = async () => {
    if (!setReady) {
      if (readiness.reason === "stale") {
        toast.error("De documentenset is verouderd. Genereer opnieuw voordat u download.");
      } else {
        toast.error("De documentenset is nog niet compleet.");
      }
      return;
    }
    setDownloadingZip(true);
    try {
      const r = await adminApi.get(
        `/admin/booking-forms/${bookingId}/document-set/zip`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/zip" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `documentenset-${String(bookingId || "").slice(0, 8)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 15_000);
      toast.success("Documentenset ZIP gedownload.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloadingZip(false);
    }
  };

  const openEmailDialog = () => {
    if (!setReady) {
      if (readiness.reason === "stale") {
        toast.error("De documentenset is verouderd. Genereer de documenten opnieuw voordat u de set naar de klant verzendt.");
      } else {
        toast.error("De documentenset is nog niet compleet. Genereer eerst de ontbrekende documenten voordat u de set naar de klant verzendt.");
      }
      return;
    }
    if (!state.customer_email) {
      toast.error("Er is geen geldig e-mailadres gevonden voor deze klant. Voeg eerst een e-mailadres toe voordat u de documentenset verzendt.");
      return;
    }
    setEmailDialogOpen(true);
  };

  const genFullSet = async () => {
    if (noItems) {
      toast.error("Voeg eerst items toe voordat je de documentenset genereert.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.post(
        `/admin/booking-forms/${bookingId}/generate-document-set`,
      );
      const results = r.data?.results || {};
      // Toon failures duidelijk (zoals waardefactuur zonder value_per_unit)
      const failed = Object.entries(results)
        .filter(([, v]) => v && v.status === "failed")
        .map(([k, v]) => `${k}: ${v.error || "onbekende fout"}`);
      if (failed.length) {
        toast.warning(`Documentenset deels gegenereerd — ${failed.join("; ")}`);
      } else {
        toast.success("Documentenset gegenereerd.");
      }
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const genOne = async (which) => {
    setBusyDoc(which);
    try {
      const path = which === "packing_list"
        ? `/admin/booking-forms/${bookingId}/generate-packing-list`
        : `/admin/booking-forms/${bookingId}/generate-value-invoice`;
      await adminApi.post(path);
      toast.success(which === "packing_list" ? "Paklijst gegenereerd." : "Waardefactuur gegenereerd.");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyDoc(null);
    }
  };

  const openPdf = async (kind, id) => {
    if (!id) return;
    // Fetch via authorised call → download to open in new tab
    try {
      const url = kind === "booking_form"
        ? `/admin/booking-forms/${id}/pdf`
        : kind === "packing_list"
        ? `/admin/packing-lists/${id}/pdf`
        : `/admin/invoices/${id}/pdf`;
      const r = await adminApi.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      // Cleanup after 30s
      setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadPdf = async (kind, id, filenameBase) => {
    if (!id) return;
    try {
      const url = kind === "booking_form"
        ? `/admin/booking-forms/${id}/pdf`
        : kind === "packing_list"
        ? `/admin/packing-lists/${id}/pdf`
        : `/admin/invoices/${id}/pdf`;
      const r = await adminApi.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${filenameBase || "document"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), 15_000);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) {
    return (
      <div className="border border-gray-200 bg-white p-6 flex items-center justify-center gap-2 text-sm text-gray-500"
           data-testid="docset-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Documentenset laden…
      </div>
    );
  }

  const bfShort = String(bookingId || "").slice(0, 8).toUpperCase();
  const pl = ds.packing_list || {};
  const vi = ds.value_invoice || {};

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}
         data-testid="booking-document-set-panel">
      {/* Stale-warning */}
      {isStale && (
        <div className="border-2 border-amber-400 bg-amber-50 p-3 flex items-start gap-2"
             data-testid="docset-stale-banner">
          <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-chivo font-bold text-amber-900">
              De documentgegevens zijn gewijzigd.
            </div>
            <div className="text-amber-800 text-xs mt-0.5">
              Genereer de documenten opnieuw om de laatste gegevens te gebruiken.
            </div>
          </div>
          {canGenerate && (
            <Button
              onClick={genFullSet} disabled={busy || noItems}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white text-xs h-8"
              data-testid="docset-regenerate-btn"
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
              Documentenset opnieuw genereren
            </Button>
          )}
        </div>
      )}

      {/* Master action bar */}
      {!isStale && canGenerate && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-700">
            <div className="font-chivo font-bold text-sm text-[#0A192F]">Documentenset</div>
            <div>
              {state.items_count} item{state.items_count === 1 ? "" : "s"}
              {state.totals?.total_cbm ? ` · CBM ${state.totals.total_cbm.toFixed(4)}` : ""}
              {state.wants_value_invoice ? ` · Waardefactuur (${state.value_invoice_currency})` : ""}
            </div>
          </div>
          <Button
            onClick={genFullSet} disabled={busy || noItems}
            className="rounded-none bg-[#0A192F] hover:bg-[#0055FF] text-white text-xs h-8"
            data-testid="docset-generate-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
            {(pl.status === "generated" || vi.status === "generated") ? "Alles opnieuw genereren" : "Documentenset genereren"}
          </Button>
        </div>
      )}

      {/* Sprint 4 — 7 Mrt 2026: verzenden / ZIP-download acties.
          Alleen tonen wanneer er items zijn (anders geen documentenset mogelijk). */}
      {!noItems && (
        <div
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 border border-gray-200 bg-white"
          data-testid="docset-send-actions"
        >
          <div className="text-xs text-gray-700">
            <div className="font-chivo font-bold text-sm text-[#0A192F]">
              Documentenset delen
            </div>
            <div>
              {setReady
                ? `Klaar om te verzenden of te downloaden${state.customer_email ? " naar " + state.customer_email : ""}.`
                : readiness.reason === "stale"
                ? "Documentenset is verouderd — genereer eerst opnieuw."
                : "Documentenset nog niet compleet — genereer eerst de ontbrekende documenten."}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canGenerate && (
              <Button
                onClick={openEmailDialog}
                disabled={!setReady || !state.customer_email}
                className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
                data-testid="docset-email-btn"
              >
                <Mail className="w-3 h-3 mr-1" />
                Documentenset naar klant e-mailen
              </Button>
            )}
            <Button
              variant="outline"
              onClick={downloadZip}
              disabled={!setReady || downloadingZip}
              className="rounded-none text-xs h-8"
              data-testid="docset-zip-btn"
            >
              {downloadingZip
                ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                : <Archive className="w-3 h-3 mr-1" />}
              Documentenset als ZIP downloaden
            </Button>
          </div>
        </div>
      )}
      {noItems && (
        <div className="text-xs text-gray-500 italic p-2" data-testid="docset-no-items-hint">
          Voeg eerst items toe aan het boekingsformulier voordat je een documentenset kunt genereren.
        </div>
      )}

      {/* 1. Boekingsformulier — altijd on-demand beschikbaar */}
      <DocRow
        testid="docset-row-booking-form"
        icon={FileText}
        title="Boekingsformulier"
        description={`Nr. ${bfShort}`}
        status="generated_on_demand"
        actions={
          <>
            <Button variant="outline" size="sm"
                    onClick={() => openPdf("booking_form", bookingId)}
                    className="rounded-none text-xs h-7 px-2"
                    data-testid="docset-open-booking">
              <ExternalLink className="w-3 h-3 mr-1" /> Openen
            </Button>
            <Button variant="outline" size="sm"
                    onClick={() => downloadPdf("booking_form", bookingId, `boekingsformulier-${bfShort}`)}
                    className="rounded-none text-xs h-7 px-2"
                    data-testid="docset-download-booking">
              <Download className="w-3 h-3" />
            </Button>
          </>
        }
      />

      {/* 2. Paklijst */}
      <DocRow
        testid="docset-row-packing-list"
        icon={Package}
        title="Paklijst"
        description={pl.id ? `Nr. ${String(pl.id).slice(0, 8).toUpperCase()}` : "Nog niet aangemaakt"}
        status={pl.status || "not_generated"}
        generatedAt={pl.generated_at}
        generatedBy={pl.generated_by}
        missing={missingKinds.has("packing_list")}
        actions={
          <>
            {(pl.status === "generated" || pl.status === "stale") && pl.id && (
              <>
                <Button variant="outline" size="sm"
                        onClick={() => openPdf("packing_list", pl.id)}
                        className="rounded-none text-xs h-7 px-2"
                        data-testid="docset-open-paklijst">
                  <ExternalLink className="w-3 h-3 mr-1" /> Openen
                </Button>
                <Button variant="outline" size="sm"
                        onClick={() => downloadPdf("packing_list", pl.id, `paklijst-${String(pl.id).slice(0, 8)}`)}
                        className="rounded-none text-xs h-7 px-2"
                        data-testid="docset-download-paklijst">
                  <Download className="w-3 h-3" />
                </Button>
              </>
            )}
            {canGenerate && (
              <Button variant="outline" size="sm"
                      onClick={() => genOne("packing_list")}
                      disabled={busyDoc === "packing_list" || noItems}
                      className="rounded-none text-xs h-7 px-2"
                      data-testid="docset-generate-paklijst">
                {busyDoc === "packing_list"
                  ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  : <RefreshCcw className="w-3 h-3 mr-1" />}
                {pl.status === "not_generated" ? "Genereren" : "Opnieuw"}
              </Button>
            )}
          </>
        }
      />

      {/* 3. Waardefactuur — alleen sectie tonen als wants_value_invoice OF status !== not_generated */}
      {(state.wants_value_invoice || vi.status !== "not_generated") && (
        <DocRow
          testid="docset-row-value-invoice"
          icon={Receipt}
          title="Waardefactuur / Value Invoice"
          description={vi.invoice_number
            ? `${vi.invoice_number}`
            : (vi.id ? `Nr. ${String(vi.id).slice(0, 8).toUpperCase()}` : "Nog niet aangemaakt")}
          status={vi.status || "not_generated"}
          generatedAt={vi.generated_at}
          generatedBy={vi.generated_by}
          missing={missingKinds.has("value_invoice")}
          actions={
            <>
              {(vi.status === "generated" || vi.status === "stale") && vi.id && (
                <>
                  <Button variant="outline" size="sm"
                          onClick={() => openPdf("value_invoice", vi.id)}
                          className="rounded-none text-xs h-7 px-2"
                          data-testid="docset-open-waardefactuur">
                    <ExternalLink className="w-3 h-3 mr-1" /> Openen
                  </Button>
                  <Button variant="outline" size="sm"
                          onClick={() => downloadPdf("value_invoice", vi.id, `waardefactuur-${vi.invoice_number || String(vi.id).slice(0, 8)}`)}
                          className="rounded-none text-xs h-7 px-2"
                          data-testid="docset-download-waardefactuur">
                    <Download className="w-3 h-3" />
                  </Button>
                </>
              )}
              {canGenerate && (
                <Button variant="outline" size="sm"
                        onClick={() => genOne("value_invoice")}
                        disabled={busyDoc === "value_invoice" || noItems}
                        className="rounded-none text-xs h-7 px-2"
                        data-testid="docset-generate-waardefactuur">
                  {busyDoc === "value_invoice"
                    ? <Loader2 className="w-3 h-3 animate-spin mr-1" />
                    : <RefreshCcw className="w-3 h-3 mr-1" />}
                  {vi.status === "not_generated" ? "Genereren" : "Opnieuw"}
                </Button>
              )}
            </>
          }
        />
      )}

      <DocumentSetEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        bookingId={bookingId}
        bookingReference={bookingId}
        defaultRecipient={state.customer_email || ""}
        documents={emailPreview}
        onSent={() => refresh()}
      />
    </div>
  );
}
